import { useCallback } from 'react';
import { useKioskStore } from '@/store/kiosk-store';
import { ApiClient } from '@/lib/api-client';
import type { KioskEvent } from '@museum-kiosk/shared-types';

const client = new ApiClient();

export interface UseSession {
  startSession(): string;
}

/**
 * Starts a new kiosk session, persists the session ID to the store,
 * and fires a `kiosk.session.started` event to the config-api.
 */
export function useSession(): UseSession {
  const storeStartSession = useKioskStore((s) => s.startSession);
  const config = useKioskStore((s) => s.config);
  const jwt = useKioskStore((s) => s.jwt);

  const startSession = useCallback((): string => {
    const sessionId = storeStartSession();

    // Fire-and-forget event — do not block the UI
    if (config && jwt) {
      const event: KioskEvent = {
        event_id: crypto.randomUUID(),
        event_type: 'kiosk.session.started',
        kiosk_id: config.kiosk_id,
        session_id: sessionId,
        location_id: config.location_id,
        timestamp: new Date().toISOString(),
      };

      void client
        .post(`/api/v1/kiosks/${config.kiosk_id}/events`, { events: [event] })
        .catch(() => {
          // Event delivery failure is non-critical
        });
    }

    return sessionId;
  }, [storeStartSession, config, jwt]);

  return { startSession };
}
